import { Routes, Route, Navigate } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import GlobalHeader from "./components/GlobalHeader";
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const SmsReport = lazy(() => import("./pages/SmsReport"));
const UsersReport = lazy(() => import("./pages/UsersReport"));
const TransactionsReport = lazy(() => import("./pages/TransactionsReport"));
const ChatReport = lazy(() => import("./pages/ChatReport"));
const RemediationReport = lazy(() => import("./pages/RemediationReport"));
const AnalyticsOverview = lazy(() => import("./pages/AnalyticsOverview"));
const CategoryInsights = lazy(() => import("./pages/CategoryInsights"));
const MerchantInsights = lazy(() => import("./pages/MerchantInsights"));
const ConfigManager = lazy(() => import("./pages/ConfigManager"));
const SubscriptionConfigManager = lazy(
  () => import("./pages/SubscriptionConfigManager"),
);
const SubscriptionsReport = lazy(() => import("./pages/SubscriptionsReport"));
const RevenueDashboard = lazy(() => import("./pages/RevenueDashboard"));
const ChatSimulator = lazy(() => import("./pages/ChatSimulator"));
const MediaManager = lazy(() => import("./pages/MediaManager"));
const TopicManager = lazy(() => import("./pages/TopicManager"));
const InsightsReport = lazy(() => import("./pages/InsightsReport"));
const NotificationsReport = lazy(() => import("./pages/NotificationsReport"));
const AIUsageReport = lazy(() => import("./pages/AIUsageReport"));
const AppVersionManager = lazy(() => import("./pages/AppVersionManager"));

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <GlobalHeader />
        <Routes>
          <Route
            path="/login"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <Login />
              </Suspense>
            }
          />
          <Route
            path="/signup"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <Signup />
              </Suspense>
            }
          />
          <Route
            path="/verify-email"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <VerifyEmail />
              </Suspense>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Suspense fallback={<div>Loading...</div>}>
                  <DashboardLayout>
                    <Routes>
                      <Route
                        index
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <DashboardHome />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/users"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <UsersReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/insights"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <InsightsReport />
                          </Suspense>
                        }
                      />

                      <Route
                        path="reports/notifications"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <NotificationsReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/ai-usage"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <AIUsageReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/transactions"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <TransactionsReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/chat"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <ChatReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/remediation"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <RemediationReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="reports/messages"
                        element={<Navigate to="/reports/chat" replace />}
                      />
                      <Route
                        path="analytics/overview"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <AnalyticsOverview />
                          </Suspense>
                        }
                      />
                      <Route
                        path="analytics/categories"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <CategoryInsights />
                          </Suspense>
                        }
                      />
                      <Route
                        path="analytics/merchants"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <MerchantInsights />
                          </Suspense>
                        }
                      />
                      <Route
                        path="subscriptions/plans"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <SubscriptionConfigManager />
                          </Suspense>
                        }
                      />
                      <Route
                        path="subscriptions/report"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <SubscriptionsReport />
                          </Suspense>
                        }
                      />
                      <Route
                        path="subscriptions/revenue"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <RevenueDashboard />
                          </Suspense>
                        }
                      />
                      <Route
                        path="chat-simulator"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <ChatSimulator />
                          </Suspense>
                        }
                      />
                      <Route
                        path="media-manager"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <MediaManager />
                          </Suspense>
                        }
                      />
                      <Route
                        path="topic-manager"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <TopicManager />
                          </Suspense>
                        }
                      />
                      <Route
                        path="config-manager"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <ConfigManager />
                          </Suspense>
                        }
                      />
                      <Route
                        path="app-version"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <AppVersionManager />
                          </Suspense>
                        }
                      />
                      <Route
                        path="sms"
                        element={
                          <Suspense fallback={<div>Loading...</div>}>
                            <SmsReport />
                          </Suspense>
                        }
                      />
                    </Routes>
                  </DashboardLayout>
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}
